/* NRU built-in wishlist — v4.0.
   Heart buttons on products/cards, plus the "Saved" tab of the search drawer.
   Item details (title, price, image, availability, variants) are fetched fresh
   from Shopify's product JSON so they're always clean and accurate — no page
   scraping. Storage is device-only (localStorage).
   v3 adds an "Add to cart" button to each saved item (variant-safe).
   v3.1 mounts the card heart on the card root instead of the media, so the
   theme's full-card link overlay can no longer swallow the tap.
   v3.2 covers every card style on the site: theme cards (search, related
   products), the collection-page grid, and the home page rows.
   v4 moves the panel into the drawer as a tab instead of a second sheet in
   front of it, keeps product JSON for the visit and warms it while the browser
   is idle, so opening the tab shows finished rows instead of a spinner. */
(function () {
  var KEY = 'nru_wishlist_v1';

  /* One place the panels get product JSON from, shared with the recently
     viewed tab so a product that is in both lists is only ever asked for once.
     Kept for the visit in sessionStorage, which throws on every call in
     private mode — hence the try/catch around all of it. */
  window.nruProductStore = window.nruProductStore || (function () {
    var STORE_KEY = 'nru-panel-products';
    var STORE_LIMIT = 60;
    var MAX_IN_FLIGHT = 3;

    var held = Object.create(null);
    /* Handles that could not be read this page but may well be fine next time
       -- a dropped connection, a 500. Held in memory only, so the next page
       gives each of them one more go. */
    var unreachable = Object.create(null);
    var waiting = Object.create(null);
    var queue = [];
    var inFlight = 0;

    try {
      var raw = window.sessionStorage.getItem(STORE_KEY);
      var saved = raw ? JSON.parse(raw) : null;
      if (saved && typeof saved === 'object') {
        for (var k in saved) {
          if (Object.prototype.hasOwnProperty.call(saved, k)) held[k] = saved[k];
        }
      }
    } catch (e) {}

    function persist() {
      try {
        var keys = Object.keys(held);
        while (keys.length > STORE_LIMIT) delete held[keys.shift()];
        window.sessionStorage.setItem(STORE_KEY, JSON.stringify(held));
      } catch (e) {}
    }

    function shape(p) {
      return {
        title: p.title || '',
        price: typeof p.price === 'number' ? p.price : null,
        priceMax: typeof p.price_max === 'number' ? p.price_max : null,
        varies: !!p.price_varies,
        compare: p.compare_at_price || 0,
        image: p.featured_image || (p.images && p.images[0]) || '',
        available: p.available !== false,
        variants: (p.variants || []).map(function (v) {
          return { id: v.id, title: v.title, available: v.available !== false };
        })
      };
    }

    function send(path, entry) {
      fetch(path + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) {
            var refused = new Error('unavailable');
            /* 404/410 is the shop saying the product is not there any more;
               every other failure is about this request, not this product. */
            refused.gone = r.status === 404 || r.status === 410;
            throw refused;
          }
          return r.json();
        })
        .then(function (p) {
          var data = shape(p);
          held[path] = data;
          persist();
          return data;
        })
        .catch(function (err) {
          /* Remember the disappointment either way. Without this a single
             deleted saved item puts a request on every repaint of the panel,
             for the whole visit. A product the shop says is gone is settled
             for the visit; anything else is only settled for this page. */
          var data = { missing: true };
          if (err && err.gone) {
            data.gone = true;
            held[path] = data;
            persist();
          } else {
            unreachable[path] = data;
          }
          return data;
        })
        .then(function (data) {
          inFlight--;
          delete waiting[path];
          entry.resolve(data);
          pump();
        });
    }

    function pump() {
      while (inFlight < MAX_IN_FLIGHT && queue.length) {
        var path = queue.shift();
        var entry = waiting[path];
        if (!entry) continue;
        inFlight++;
        send(path, entry);
      }
    }

    function enqueue(path, first) {
      var entry = waiting[path];
      if (entry) {
        if (first) {
          var at = queue.indexOf(path);
          if (at > 0) {
            queue.splice(at, 1);
            queue.unshift(path);
          }
        }
        return entry.promise;
      }
      entry = {};
      entry.promise = new Promise(function (resolve) {
        entry.resolve = resolve;
      });
      waiting[path] = entry;
      if (first) queue.unshift(path);
      else queue.push(path);
      pump();
      return entry.promise;
    }

    function whenIdle(fn) {
      if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2000 });
      else setTimeout(fn, 400);
    }

    return {
      peek: function (path) {
        return held[path] || unreachable[path] || null;
      },
      load: function (path) {
        var known = held[path] || unreachable[path];
        if (known) return Promise.resolve(known);
        return enqueue(path, true);
      },
      /* Warm the rows a shopper is likely to open next while nothing else is
         happening, a few at a time and never twice for the same product. */
      warm: function (paths) {
        if (!paths || !paths.length) return;
        whenIdle(function () {
          for (var i = 0; i < paths.length; i++) {
            var path = paths[i];
            if (!path || held[path] || unreachable[path] || waiting[path]) continue;
            enqueue(path, false);
          }
        });
      }
    };
  })();

  var store = window.nruProductStore;

  /* Every product-card shape used on this store. */
  var CARD_SELECTOR = '.card-wrapper.product-card-wrapper, [data-product-card], .nru-row__card';
  var TITLE_SELECTOR = '.card__heading a, .nru-row__product-title a, h3 a, .card__heading';

  /* Entries saved before the key was canonicalised are collection-scoped
     (/collections/x/products/y), so the same product could be stored twice.
     Normalise every entry to /products/<handle> on read, keep the earliest
     of any duplicates, and persist the cleaned list once per page. Anything
     unreadable is skipped rather than thrown on, and a value that is not a
     list at all is left alone instead of being rewritten. */
  var migrated = false;
  function normalise(list) {
    var out = [];
    var seen = {};
    for (var i = 0; i < list.length; i++) {
      var it = list[i];
      if (!it || typeof it !== 'object') continue;
      var handle = handleFromUrl(it.url) || (typeof it.handle === 'string' ? it.handle : '');
      if (!handle || seen['h:' + handle]) continue;
      seen['h:' + handle] = true;
      out.push({ url: urlFor(handle), handle: handle, title: it.title || '' });
    }
    return out;
  }
  function read() {
    var raw;
    try { raw = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; }
    if (!raw || !Array.isArray(raw)) return [];
    var list = normalise(raw);
    if (!migrated) {
      migrated = true;
      if (JSON.stringify(list) !== JSON.stringify(raw)) {
        try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
      }
    }
    return list;
  }
  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    refreshHearts();
    refreshCounts();
    if (activeTab() === 'saved') renderSaved();
  }
  function has(url) { var u = canonicalUrl(url); return !!u && read().some(function (i) { return i.url === u; }); }
  function toggle(item) {
    var handle = item.handle || handleFromUrl(item.url);
    if (!handle) return false;
    var url = urlFor(handle);
    var list = read();
    var idx = -1;
    for (var i = 0; i < list.length; i++) { if (list[i].url === url) { idx = i; break; } }
    if (idx > -1) list.splice(idx, 1);
    else list.unshift({ url: url, handle: handle, title: item.title || '' });
    write(list);
    return idx === -1;
  }
  function esc(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /* Shopify's server-rendered `| money` groups thousands ($1,234.56); a plain
     toFixed(2) drops the separator on every AJAX repaint, so format with Intl
     in the shop's currency and fall back to the old plain form on engines
     without Intl. */
  function shopCurrency() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return window.Shopify.currency.active;
      }
    } catch (e) {}
    return 'USD';
  }
  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: shopCurrency()
      }).format((cents || 0) / 100);
    } catch (e) {
      return '$' + ((cents || 0) / 100).toFixed(2);
    }
  }
  function money(cents) { if (cents == null || isNaN(cents)) return ''; return formatMoney(cents); }
  function sizedImg(src, w) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function handleFromUrl(url) { var m = String(url || '').match(/\/products\/([^/?#]+)/); return m ? m[1] : ''; }
  /* Always store the canonical /products/<handle> path: the same product is
     reachable as /collections/x/products/y, and that must not become a
     second entry (or miss a de-duplication). */
  function urlFor(handle) { return '/products/' + handle; }
  function canonicalUrl(url) { var h = handleFromUrl(url); return h ? urlFor(h) : ''; }
  function textOf(el) { return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : ''; }

  function drawer() { return document.querySelector('nru-floating-search'); }
  function activeTab() { var d = drawer(); return d ? d.activeTab : ''; }
  /* A count appearing or disappearing changes how wide its tab is. */
  function nudgeIndicator() {
    var d = drawer();
    if (d && typeof d.moveIndicator === 'function') d.moveIndicator();
  }

  function heartSVG() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>';
  }

  function makeBtn(item, cls) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'nru-wish-btn' + (cls ? ' ' + cls : '');
    b.setAttribute('aria-label', 'Save to wishlist');
    b.innerHTML = heartSVG();
    b.setAttribute('data-nru-wish-url', item.url);
    if (has(item.url)) b.classList.add('is-saved');
    b.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      b.classList.toggle('is-saved', toggle(item));
    });
    /* Some cards navigate or swap images on pointerdown, before the click lands. */
    ['pointerdown', 'mousedown', 'touchstart'].forEach(function (evt) {
      b.addEventListener(evt, function (e) { e.stopPropagation(); }, { passive: true });
    });
    return b;
  }

  function injectCards() {
    var cards = document.querySelectorAll(CARD_SELECTOR);
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.querySelector('.nru-wish-btn')) continue;
      if (card.closest && card.closest('.nru-panel-row')) continue;
      var link = card.querySelector('a[href*="/products/"]');
      if (!link) continue;
      var handle = handleFromUrl(link.getAttribute('href'));
      if (!handle) continue;
      var item = {
        url: urlFor(handle),
        handle: handle,
        title: textOf(card.querySelector(TITLE_SELECTOR)) || textOf(link)
      };
      /* Mount on the card root rather than the media wrapper: the media clips
         its overflow, and on theme cards a full-card link overlay sits above
         it, which made taps open the product instead of saving it. */
      if (getComputedStyle(card).position === 'static') card.style.position = 'relative';
      card.appendChild(makeBtn(item, 'nru-wish-btn--card'));
    }
  }

  function injectProduct() {
    if (!/\/products\//.test(location.pathname)) return;
    if (document.querySelector('.nru-wish-btn--pdp')) return;
    var title = document.querySelector('.product__title');
    if (!title) return;
    var handle = handleFromUrl(location.pathname);
    if (!handle) return;
    var item = { url: urlFor(handle), handle: handle, title: textOf(title) };
    var wrap = document.createElement('div');
    wrap.className = 'nru-wish-pdp-wrap';
    wrap.appendChild(makeBtn(item, 'nru-wish-btn--pdp'));
    title.parentNode.insertBefore(wrap, title.nextSibling);
  }

  function refreshHearts() {
    var hearts = document.querySelectorAll('.nru-wish-btn');
    for (var i = 0; i < hearts.length; i++) {
      hearts[i].classList.toggle('is-saved', has(hearts[i].getAttribute('data-nru-wish-url')));
    }
  }
  function refreshCounts() {
    var n = read().length;
    var els = document.querySelectorAll('[data-nru-wish-count]');
    var moved = false;
    for (var i = 0; i < els.length; i++) {
      if (els[i].textContent !== String(n)) els[i].textContent = String(n);
      var disp = n > 0 ? 'inline-flex' : 'none';
      if (els[i].style.display !== disp) {
        els[i].style.display = disp;
        moved = true;
      }
    }
    if (moved) nudgeIndicator();
  }

  function addToCart(variantId, qty) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: qty || 1 })
    }).then(function (r) {
      return r.json().then(function (j) {
        /* The drawer watches every cart request itself and updates its own
           badge, the header bubble and the cart rows from the response, so
           there is nothing to fetch again here. */
        if (r.ok && j && j.id) {
          try { document.dispatchEvent(new CustomEvent('cart:item-added')); } catch (e) {}
        }
        return { ok: r.ok, body: j };
      });
    });
  }

  /* ---------- saved panel ---------- */

  /* One builder for both panel tabs, so a saved row and a recently viewed row
     are the same shape — and, more to the point, so a placeholder row is
     exactly as tall as the finished row that replaces it and nothing below it
     ever moves. */
  window.nruPanelRow = window.nruPanelRow || {
    /* The photo box follows the shared photo swap's contract: the product path
       on the box, the picture as its only child, a definite size from the
       stylesheet, and no positioning of our own. */
    media: function (url, data) {
      if (data && data.image) {
        return '<div class="nru-panel-row__media nru-panel-media" data-image-toggle data-product-url="' +
          esc(url) + '"><img src="' + esc(sizedImg(data.image, 160)) +
          '" alt="" width="64" height="64" loading="lazy" decoding="async"></div>';
      }
      return '<div class="nru-panel-row__media"></div>';
    },

    price: function (data) {
      var now = money(data.price);
      if (!data.available) return '<span class="nru-panel-row__gone">' + esc(now) + '</span>';
      if (data.compare && data.price && data.compare > data.price) {
        return '<span class="nru-panel-row__now">' + esc(now) + '</span>' +
          '<s class="nru-panel-row__was">' + esc(money(data.compare)) + '</s>';
      }
      if (data.varies && data.priceMax && data.priceMax !== data.price) {
        return '<span class="nru-panel-row__now">' + esc(now + '\u2013' + money(data.priceMax)) + '</span>';
      }
      return '<span class="nru-panel-row__now">' + esc(now) + '</span>';
    },

    controls: function (data, prefix) {
      var avail = data.available ? (data.variants || []).filter(function (v) { return v.available; }) : [];
      if (!avail.length) return '<span class="nru-panel-row__soldout">Sold out</span>';
      var picker;
      if (avail.length > 1) {
        picker = '<select class="nru-panel-row__variant" data-nru-' + prefix +
          '-variant aria-label="Choose an option for ' + esc(data.title) + '">' +
          avail.map(function (v) { return '<option value="' + esc(v.id) + '">' + esc(v.title) + '</option>'; }).join('') +
          '</select>';
      } else {
        picker = '<input type="hidden" data-nru-' + prefix + '-variant-single value="' + esc(avail[0].id) + '">';
      }
      return picker + '<button type="button" class="nru-panel-row__add" data-nru-' + prefix +
        '-add>Add to cart</button>';
    },

    info: function (opts) {
      var data = opts.data;
      /* The shop no longer has this product. No link to a page that is not
         there any more -- just the name it was saved under and, on the saved
         tab, the button that takes it off the list. */
      if (data && data.gone) {
        return '<span class="nru-panel-row__title">' + esc(opts.title || 'This product') + '</span>' +
          '<span class="nru-panel-row__price"></span>' +
          '<div class="nru-panel-row__controls"><span class="nru-panel-row__note">No longer available</span></div>';
      }
      /* Could not be read this time round. The product is very probably still
         there, so the row keeps its link and says so plainly. */
      if (!data || data.missing) {
        return '<a class="nru-panel-row__title" href="' + esc(opts.url) + '">' +
          esc(opts.title || 'View product') + '</a>' +
          '<span class="nru-panel-row__price"></span>' +
          '<div class="nru-panel-row__controls"><span class="nru-panel-row__note">Unavailable right now</span></div>';
      }
      return '<a class="nru-panel-row__title" href="' + esc(opts.url) + '">' +
        esc(data.title || opts.title) + '</a>' +
        '<span class="nru-panel-row__price">' + this.price(data) + '</span>' +
        '<div class="nru-panel-row__controls">' + this.controls(data, opts.prefix) + '</div>';
    },

    placeholder: function () {
      return '<span class="nru-panel-row__title"><span class="nru-skel-bar" style="width:74%"></span></span>' +
        '<span class="nru-panel-row__price"><span class="nru-skel-bar" style="width:34%"></span></span>' +
        '<div class="nru-panel-row__controls"><span class="nru-skel-bar"></span></div>';
    },

    remove: function (opts) {
      if (!opts.remove) return '';
      return '<button type="button" class="nru-panel-row__remove" data-nru-' + opts.prefix + '-remove="' +
        esc(encodeURIComponent(opts.url)) + '" aria-label="Remove ' + esc(opts.title || 'item') + '">&times;</button>';
    },

    build: function (opts) {
      return '<div class="nru-panel-row' + (opts.remove ? ' nru-panel-row--removable' : '') +
        (opts.data ? '' : ' is-loading') + '" data-url="' + esc(opts.url) + '">' +
        this.media(opts.url, opts.data) +
        '<div class="nru-panel-row__info">' + (opts.data ? this.info(opts) : this.placeholder()) + '</div>' +
        this.remove(opts) +
        '</div>';
    },

    /* Filled in place: the row element itself is never replaced, so the rows
       below it cannot move. */
    fill: function (row, opts) {
      row.classList.remove('is-loading');
      row.innerHTML = this.media(opts.url, opts.data) +
        '<div class="nru-panel-row__info">' + this.info(opts) + '</div>' +
        this.remove(opts);
    },

    empty: function (opts) {
      return '<div class="nru-panel-empty">' + opts.icon +
        '<p class="nru-panel-empty__title">' + esc(opts.title) + '</p>' +
        '<p class="nru-panel-empty__text">' + esc(opts.text) + '</p>' +
        '<button type="button" class="nru-panel-empty__btn" data-nru-panel-search>' + esc(opts.action) + '</button>' +
        '</div>';
    }
  };

  var HEART_ICON =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 20.3l-1.3-1.19C6 14.87 3 12.09 3 8.7 3 5.94 5.19 3.75 7.95 3.75c1.56 0 3.06.73 4.05 1.88a5.36 5.36 0 014.05-1.88C18.81 3.75 21 5.94 21 8.7c0 3.39-3 6.17-7.7 10.42L12 20.3z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';

  var renderToken = 0;
  function renderSaved() {
    var body = document.querySelector('[data-nru-saved-body]');
    var kit = window.nruPanelRow;
    if (!body || !kit) return;
    var toolbar = document.querySelector('[data-nru-saved-toolbar]');
    var summary = document.querySelector('[data-nru-saved-summary]');
    var list = read();

    if (toolbar) toolbar.hidden = !list.length;
    if (summary) summary.textContent = list.length + (list.length === 1 ? ' saved item' : ' saved items');

    if (!list.length) {
      body.innerHTML = kit.empty({
        icon: HEART_ICON,
        title: 'Nothing saved yet',
        text: 'Tap the heart on any product and it will be waiting for you here.',
        action: 'Browse products'
      });
      return;
    }

    var token = ++renderToken;
    /* Every row is on screen at its finished height before a single request
       goes out, so the panel never grows or jumps as the data arrives. */
    body.innerHTML = list
      .map(function (entry) {
        return kit.build({
          url: entry.url,
          title: entry.title,
          data: store.peek(entry.url),
          prefix: 'wish',
          remove: true
        });
      })
      .join('');

    var rows = body.querySelectorAll('.nru-panel-row');
    list.forEach(function (entry, i) {
      var row = rows[i];
      if (!row || !row.classList.contains('is-loading')) return;
      store.load(entry.url).then(function (data) {
        if (token !== renderToken || !row.parentNode) return;
        kit.fill(row, { url: entry.url, title: entry.title, data: data, prefix: 'wish', remove: true });
      });
    });
  }

  function handleAdd(row, btn) {
    var sel = row.querySelector('[data-nru-wish-variant]');
    var single = row.querySelector('[data-nru-wish-variant-single]');
    var id = sel ? sel.value : (single ? single.value : null);
    if (!id) return;
    btn.disabled = true;
    var original = btn.textContent;
    btn.textContent = 'Adding…';
    addToCart(id, 1).then(function (res) {
      if (res.ok && res.body && res.body.id) {
        btn.textContent = 'Added ✓';
        btn.classList.add('is-added');
        setTimeout(function () { btn.disabled = false; btn.textContent = original; btn.classList.remove('is-added'); }, 1600);
      } else {
        btn.textContent = 'Unavailable';
        setTimeout(function () { btn.disabled = false; btn.textContent = original; }, 1600);
      }
    }).catch(function () {
      btn.textContent = 'Try again';
      setTimeout(function () { btn.disabled = false; btn.textContent = original; }, 1600);
    });
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var addBtn = t.closest('[data-nru-wish-add]');
    if (addBtn) {
      var row = addBtn.closest('.nru-panel-row');
      if (row) handleAdd(row, addBtn);
      return;
    }
    var rem = t.closest('[data-nru-wish-remove]');
    if (rem) {
      var url = canonicalUrl(decodeURIComponent(rem.getAttribute('data-nru-wish-remove') || ''));
      write(read().filter(function (x) { return !url || x.url !== url; }));
      renderSaved();
    }
  });

  document.addEventListener('nru:panel', function (e) {
    if (e.detail && e.detail.tab === 'saved') renderSaved();
  });

  function inject() { injectCards(); injectProduct(); }
  function boot() {
    inject();
    refreshCounts();
    renderSaved();
    store.warm(read().slice(0, 10).map(function (entry) { return entry.url; }));
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  try {
    var mo = new MutationObserver(function () {
      clearTimeout(window.__nruWishTimer);
      window.__nruWishTimer = setTimeout(inject, 300);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  } catch (e) {}
})();
